import { Button, type ButtonProps } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AddLine,
  AttachLine,
  CollectionLine,
  FileLine,
  GlobeLine,
} from "@dropbox/dig-icons/assets";
import { PAP_Click_DashChatAction } from "@mirage/analytics/events/types/click_dash_chat_action";
import {
  DashSearch,
  NoSearch,
  SearchParams,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/context_engine_conversations_service_pb";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import i18n, { Trans } from "@mirage/translations";
import { memo, useEffect, useMemo } from "react";
import { getAllReferencedSources } from "../../../utils/messageSources";
import styles from "./ContextPicker.module.css";

import type { UseMenuValueType } from "@dropbox/dig-components/hooks";
import { Box } from "@dropbox/dig-foundations";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { Link } from "@mirage/link/Link";
import { useScopedSources } from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useManuallyAddedSources";
import {
  type ModelId,
  type UserMessageOptions,
} from "@mirage/service-conversation/types";
import { BetaBadge } from "@mirage/shared/badges/BetaBadge";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import { usePrevious } from "@mirage/shared/util/hooks";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import type { ChatConversationMessage, ChatSource } from "../../../types";
import { ContextPickerSourceRow } from "./ContextPickerSourceRow";

export type SearchTargetCase = "noSearch" | "dashSearch" | "webSearch";

/**
 * Interface for the selection value when picking a search context option.
 */
type SearchTargetContextMode = {
  value: NoSearch | DashSearch;
  case: SearchTargetCase;
  type: "searchTarget";
};

// Type for actual gRPC calls (excludes webSearch)
type ValidSearchTargetCase = "noSearch" | "dashSearch";
type ValidContextMode = {
  value: NoSearch | DashSearch;
  case: ValidSearchTargetCase;
};

interface ContextPickerProps {
  userMessageOptions?: UserMessageOptions;
  setUserMessageOptions: (options: UserMessageOptions) => void;
  size?: ButtonProps["size"];
  logComposeEvent?: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  conversationId?: string;
  displayMessages: ChatConversationMessage[];
  manuallyAddedSources?: ChatSource[];
  isCompact?: boolean;
  selectedModel?: ModelId;
  setSelectedModel?: (model: ModelId) => void;
  handleClickAdd?: () => void;
  assistRequestId?: string;
  searchSessionId?: string;
}
export const ContextPicker = memo(
  ({
    userMessageOptions,
    setUserMessageOptions,
    size = "medium",
    logComposeEvent,
    conversationId,
    displayMessages,
    manuallyAddedSources = [],
    isCompact = false,
    selectedModel,
    setSelectedModel,
    handleClickAdd,
    assistRequestId,
    searchSessionId,
  }: ContextPickerProps) => {
    const isWebSearchEnabled = useFeatureFlagValueBool(
      "dash_2025_05_27_assist_web_search",
    );
    const isFolderScopedChatEnabled = useFeatureFlagValueBool(
      "bridge_fss_chat_2025_07_28_folder_scoped_chat",
    );

    const isEmbed = isEmbedPage();
    const showWebSearchOption = isWebSearchEnabled && !isEmbed;

    const { scopedSources } = useScopedSources();
    const dashSearchLabel = i18n.t("context_picker_dash_search_label");

    const fssFiles = useMemo(() => {
      return scopedSources.filter((source) => source.type === "fss_file");
    }, [scopedSources]);

    const fssFolder = useMemo(() => {
      return scopedSources.find((source) => source.type === "fss_folder");
    }, [scopedSources]);

    const sourcesList = useMemo(() => {
      const referencedSources = getAllReferencedSources(displayMessages);
      const referencedChatSources = Array.from(referencedSources.values());
      const seenSourceUUIDs = new Set<string>();
      const allChatSources = [
        ...referencedChatSources,
        ...manuallyAddedSources,
        ...scopedSources,
      ];
      const dedupeChatSources: ChatSource[] = [];
      for (const source of allChatSources) {
        const sourceUUID = getSourceUUID(source);
        // dedupe sources based on UUID
        // and ensure that we don't include the FSS folder itself as a source
        if (
          sourceUUID &&
          !seenSourceUUIDs.has(sourceUUID) &&
          fssFolder?.fileId !== sourceUUID
        ) {
          seenSourceUUIDs.add(sourceUUID);
          dedupeChatSources.push(source);
        }
      }
      return dedupeChatSources;
    }, [displayMessages, manuallyAddedSources, scopedSources, fssFolder]);

    const currentChatSourceCount = sourcesList.length;

    let noSearchOptionLabel =
      currentChatSourceCount > 0
        ? i18n.t("compose_num_sources", { count: currentChatSourceCount })
        : i18n.t("context_picker_no_search_label");
    let noSearchButtonLabel =
      currentChatSourceCount > 0
        ? i18n.t("compose_num_sources", { count: currentChatSourceCount })
        : i18n.t("context_picker_no_search_label");
    let noSearchOptionDescription = i18n.t(
      "context_picker_no_search_description",
    );

    if (isEmbed) {
      if (fssFolder && isFolderScopedChatEnabled) {
        noSearchOptionLabel = i18n.t("this_folder");
        noSearchButtonLabel =
          currentChatSourceCount > 0
            ? i18n.t("this_folder_and_sources", {
                count: currentChatSourceCount,
              })
            : i18n.t("this_folder");
        noSearchOptionDescription = fssFolder.name || "";
      } else if (fssFiles.length === 1) {
        noSearchOptionLabel = i18n.t("this_file");
        noSearchButtonLabel =
          currentChatSourceCount > 1
            ? i18n.t("this_file_and_sources", {
                count: currentChatSourceCount - 1, // Since we are deduping above; excluding the single file itself like this should be fine.
              })
            : i18n.t("this_file");
        noSearchOptionDescription = fssFiles[0].name;
      }
    }

    useAutoDisableDashSearch(
      userMessageOptions,
      setUserMessageOptions,
      manuallyAddedSources,
    );

    const { buttonLabel, menuOptions, icon } = useMemo(() => {
      const effectiveSearchType = getCurrentSearchType(
        userMessageOptions,
        selectedModel,
        isFolderScopedChatEnabled,
      );
      return {
        buttonLabel: getMenuButtonLabel(
          effectiveSearchType,
          dashSearchLabel,
          noSearchButtonLabel,
        ),
        menuOptions: getMenuOptions(
          effectiveSearchType,
          showWebSearchOption,
          isEmbed,
          dashSearchLabel,
          noSearchOptionLabel,
          noSearchOptionDescription,
        ),
        icon: getMenuButtonIcon(effectiveSearchType),
      };
    }, [
      userMessageOptions,
      selectedModel,
      showWebSearchOption,
      dashSearchLabel,
      noSearchOptionLabel,
      noSearchOptionDescription,
      noSearchButtonLabel,
      isFolderScopedChatEnabled,
      isEmbed,
    ]);

    return (
      <Menu.Wrapper
        onSelection={(selection?: UseMenuValueType) => {
          if (logComposeEvent) {
            logComposeEvent(
              PAP_Click_DashChatAction({
                featureLine: "assist",
                dashChatAction: "change_context",
                conversationId,
                chatContextType:
                  selection?.case === "noSearch" ? "no_search" : "all_content",
              }),
            );
          }
          if (isSearchTargetContextMode(selection)) {
            // Handle web search case: set to noSearch and change model to GPT-4o Search Preview
            if (selection.case === "webSearch") {
              setUserMessageOptions({
                ...userMessageOptions,
                searchParams: new SearchParams({
                  searchTarget: {
                    value: new NoSearch(),
                    case: "noSearch",
                  },
                }),
              });
              // Set model to GPT-4o Search Preview if setSelectedModel is available
              if (setSelectedModel) {
                setSelectedModel("openai:gpt-4o-search-preview");
              }
            } else {
              // Handle normal cases (dashSearch, noSearch)
              // Cast to valid type since we know webSearch is handled above
              const validSelection = selection as ValidContextMode;

              if (
                fssFolder &&
                validSelection.case === "noSearch" &&
                isFolderScopedChatEnabled
              ) {
                // Folder scope requires dash search, but we want to show the
                // "noSearch" equivalent selection in the UI, using fssSearchScope
                // to control the scope itself.
                setUserMessageOptions({
                  ...userMessageOptions,
                  searchParams: new SearchParams({
                    searchTarget: {
                      case: "dashSearch",
                      value: new DashSearch(),
                    },
                  }),
                  fssSearchScope: fssFolder.path,
                });
              } else {
                setUserMessageOptions({
                  ...userMessageOptions,
                  searchParams: new SearchParams({
                    searchTarget: validSelection,
                  }),
                  fssSearchScope: undefined,
                });
              }
              // Reset model to "auto" when selecting noSearch or dashSearch
              if (setSelectedModel) {
                setSelectedModel("");
              }
            }
          }
        }}
        className={isCompact ? styles.CompactMenuMargin : undefined}
      >
        {({ getContentProps, getTriggerProps }) => (
          <>
            <Button
              {...getTriggerProps()}
              variant="transparent"
              size={size}
              withDropdownIcon
              hasNoUnderline
              className={styles.MenuButton}
            >
              {isCompact ? <UIIcon src={icon} /> : buttonLabel}
            </Button>
            <Menu.Content
              {...getContentProps()}
              auto
              placement="top-end"
              className={styles.MenuContent}
            >
              <Menu.Segment withLabel={i18n.t("get_answers_from")}>
                {menuOptions.map((option) => (
                  <Menu.SelectItem
                    key={option.value.case}
                    value={option.value}
                    selected={option.isSelected}
                    withRightAccessory={option.icon}
                  >
                    <div className={styles.MenuItemContent}>
                      <Text className={styles.MenuItemLabel}>
                        {option.label}
                      </Text>
                      <Text className={styles.MenuItemDescription} size="small">
                        {option.description}
                      </Text>
                    </div>
                  </Menu.SelectItem>
                ))}
              </Menu.Segment>
              {sourcesList.length > 0 && (
                <Menu.Segment
                  withLabel={i18n.t("compose_num_sources", {
                    count: sourcesList.length,
                  })}
                  className={styles.SourcesSegment}
                >
                  {sourcesList.map((source, index) => (
                    <ContextPickerSourceRow
                      key={index}
                      source={source}
                      logComposeEvent={logComposeEvent}
                      conversationId={conversationId}
                      searchSessionId={searchSessionId}
                      assistRequestId={assistRequestId}
                    />
                  ))}
                </Menu.Segment>
              )}
              <Menu.Segment>
                <Menu.ActionItem
                  withTitle={i18n.t("add_sources")}
                  withLeftAccessory={
                    <UIIcon src={isEmbedPage() ? AttachLine : AddLine} />
                  }
                  onClick={handleClickAdd}
                ></Menu.ActionItem>
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    );
  },
);
ContextPicker.displayName = "ContextPicker";

function isSearchTargetContextMode(
  selection: UseMenuValueType,
): selection is SearchTargetContextMode {
  return selection && selection.type === "searchTarget";
}

interface MenuOption {
  value: SearchTargetContextMode;
  label: string | React.ReactNode;
  title: string;
  description: string | React.ReactNode;
  isSelected: boolean;
  icon: React.ReactNode;
}

function getMenuOptions(
  effectiveSearchType: SearchTargetCase | undefined,
  showWebSearchOption: boolean,
  isPageEmbed: boolean,
  dashSearchLabel: string,
  noSearchOptionLabel: string,
  noSearchOptionDescription: string,
): MenuOption[] {
  const noSearchOption: MenuOption = {
    value: { value: new NoSearch(), case: "noSearch", type: "searchTarget" },
    label: noSearchOptionLabel,
    title: noSearchOptionLabel,
    description: noSearchOptionDescription,
    isSelected: effectiveSearchType === "noSearch",
    icon: <UIIcon src={FileLine} />,
  };

  const dashSearchOption: MenuOption = {
    value: {
      value: new DashSearch(),
      case: "dashSearch",
      type: "searchTarget",
    },
    label: dashSearchLabel,
    title: dashSearchLabel,
    description: isPageEmbed
      ? i18n.t("context_picker_dash_search_description_for_fss")
      : i18n.t("context_picker_dash_search_description"),
    isSelected: effectiveSearchType === "dashSearch",
    icon: <UIIcon src={CollectionLine} />,
  };

  const webSearchOption: MenuOption = {
    value: { value: new NoSearch(), case: "webSearch", type: "searchTarget" },
    label: (
      <Box display="flex" alignItems="center">
        {i18n.t("context_picker_web_search_label")}
        <BetaBadge className={styles.BetaBadge} />
      </Box>
    ),
    title: i18n.t("context_picker_web_search_label"),
    description: (
      <Trans
        i18nKey="context_picker_web_search_description"
        components={{
          model: (
            <Link
              href="https://platform.openai.com/docs/models/gpt-4o-search-preview"
              target="_blank"
              rel="noopener noreferrer"
            />
          ),
        }}
      />
    ),
    isSelected: effectiveSearchType === "webSearch",
    icon: <UIIcon src={GlobeLine} />,
  };

  return showWebSearchOption
    ? [dashSearchOption, noSearchOption, webSearchOption]
    : [dashSearchOption, noSearchOption];
}

function getCurrentSearchType(
  userMessageOptions: UserMessageOptions | undefined,
  selectedModel: ModelId | undefined,
  isFolderScopedChatEnabled: boolean,
): SearchTargetCase | undefined {
  let currentSearchType = userMessageOptions?.searchParams?.searchTarget?.case;

  // If `fssSearchScope` is set, we must render the "noSearch" option in the UI.
  // We have to fake this here, because the userMessageOptions object must reflect
  // what'll actually be sent to the backend.
  if (userMessageOptions?.fssSearchScope && isFolderScopedChatEnabled) {
    currentSearchType = "noSearch";
  }

  // Detect if we're in "web search mode" (noSearch + GPT-4o Search Preview model)
  const isWebSearchMode =
    currentSearchType === "noSearch" &&
    selectedModel === "openai:gpt-4o-search-preview";

  // Override search type if we're in web search mode
  return isWebSearchMode ? "webSearch" : currentSearchType;
}

function getMenuButtonLabel(
  effectiveSearchType: SearchTargetCase | undefined,
  dashSearchLabel: string,
  noSearchLabel: string,
) {
  return effectiveSearchType === "noSearch"
    ? noSearchLabel
    : effectiveSearchType === "webSearch"
      ? i18n.t("context_picker_web_search_label")
      : dashSearchLabel;
}

function getMenuButtonIcon(effectiveSearchType: SearchTargetCase | undefined) {
  if (effectiveSearchType === undefined) {
    return CollectionLine;
  }
  switch (effectiveSearchType) {
    case "noSearch":
      return FileLine;
    case "webSearch":
      return GlobeLine;
    case "dashSearch":
      return CollectionLine;
    default:
      effectiveSearchType satisfies never;
      throw new Error("Invalid search type");
  }
}

/**
 * Auto-disable Dash search when sources are first added, i.e. if Dash search was enabled AND user
 * has just manually added sources / browser tabs (from 0 to > 0).
 */
export function useAutoDisableDashSearch(
  userMessageOptions: UserMessageOptions | undefined,
  setUserMessageOptions: (options: UserMessageOptions) => void,
  manuallyAddedSources: ChatSource[],
) {
  const manuallyAddedSourcesCount = manuallyAddedSources.length;
  const numTabsSelected =
    userMessageOptions?.browserContext?.selectedTabIDs?.size ?? 0;
  const sourceCount = manuallyAddedSourcesCount + numTabsSelected;

  const prevSourceCount = usePrevious(sourceCount);
  useEffect(() => {
    if (!(prevSourceCount === 0 && sourceCount > 0)) {
      // we only care about the cases where manuallyAddedSources goes from 0 to > 0
      return;
    }

    const currentSearchType =
      userMessageOptions?.searchParams?.searchTarget?.case;
    if (currentSearchType !== "dashSearch") {
      // we only care about the cases where Dash search is enabled
      return;
    }

    setUserMessageOptions({
      ...userMessageOptions,
      searchParams: new SearchParams({
        searchTarget: {
          value: new NoSearch(),
          case: "noSearch",
        },
      }),
    });
  }, [userMessageOptions, setUserMessageOptions, prevSourceCount, sourceCount]);
}
